import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { loadPayPalScript, initializePayPalButton, getPayPalSettings } from '../../utils/paypal';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../../config';

/**
 * PayPal button component for processing payments
 * @param {Object} props - The component props
 * @param {number} props.amount - The payment amount
 * @param {function} props.onSuccess - Callback function when payment is successful
 * @param {function} props.onError - Callback function when payment fails
 * @param {function} props.onCancel - Callback function when payment is canceled
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {string} props.examId - The ID of the exam being subscribed to
 * @param {boolean} props.autoRenew - Whether the subscription should auto-renew
 */
const PayPalButton = ({ 
    amount, 
    onSuccess, 
    onError, 
    onCancel,
    disabled = false,
    examId,
    autoRenew = false
}) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sdkLoaded, setSdkLoaded] = useState(false);
    const containerRef = useRef(null);
    const buttonInstanceRef = useRef(null);
    const navigate = useNavigate();
    const [successMessage, setSuccessMessage] = useState(null);

    // Cleanup function to remove PayPal button
    const cleanupPayPalButton = useCallback(() => {
        if (buttonInstanceRef.current) {
            try {
                buttonInstanceRef.current.close();
            } catch (err) {
                console.warn('Error cleaning up PayPal button:', err);
            }
            buttonInstanceRef.current = null;
        }
        if (containerRef.current) {
            containerRef.current.innerHTML = '';
        }
    }, []);

    // Initialize PayPal button
    const initializeButton = useCallback(async () => {
        if (!containerRef.current || !sdkLoaded || disabled) return;

        try {
            cleanupPayPalButton();

            const containerId = `paypal-button-container-${Date.now()}`;
            containerRef.current.id = containerId;

            const buttonConfig = {
                createOrder: (data, actions) => {
                    return actions.order.create({
                        purchase_units: [{
                            amount: {
                                value: amount.toString(),
                                currency_code: 'GBP'
                            }
                        }]
                    });
                },
                onApprove: async (data, actions) => {
                    try {
                        const order = await actions.order.capture();
                        console.log('Payment successful:', order);
                        console.log("Data: ", data);
                        
                        // Call the onSuccess callback with the payment data
                        if (onSuccess) {
                            onSuccess(order);
                        }
                        
                    } catch (error) {
                        console.error('Payment processing error:', error);
                        setError('There was an error processing your payment. Please try again.');
                        setSuccessMessage(null);
                        if (onError) {
                            onError(error);
                        }
                    }
                },
                onError: (err) => {
                    console.error('PayPal button error:', err);
                    onError(err);
                },
                onCancel: () => {
                    onCancel();
                }
            };

            buttonInstanceRef.current = await initializePayPalButton(containerId, buttonConfig);
            setError(null);
        } catch (err) {
            console.error('Error initializing PayPal button:', err);
            setError('Failed to initialize PayPal button. Please try again.');
            onError(err);
        }
    }, [amount, onSuccess, onError, onCancel, sdkLoaded, disabled, cleanupPayPalButton, navigate, examId, autoRenew]);

    // Load PayPal SDK
    useEffect(() => {
        let mounted = true;

        const loadSDK = async () => {
            try {
                setLoading(true);
                setError(null);

                // Get PayPal settings from server
                const settings = await getPayPalSettings();
                console.log('PayPal settings:', settings);

                await loadPayPalScript({
                    clientId: settings.clientId,
                    sandbox: settings.sandbox
                });
                if (mounted) {
                    setSdkLoaded(true);
                }
            } catch (err) {
                console.error('Error loading PayPal SDK:', err);
                if (mounted) {
                    setError('Failed to load PayPal. Please try again later.');
                    onError(err);
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadSDK();

        return () => {
            mounted = false;
            cleanupPayPalButton();
        };
    }, [onError, cleanupPayPalButton]);

    // Initialize button when SDK is loaded
    useEffect(() => {
        if (sdkLoaded && !disabled) {
            initializeButton();
        }
    }, [sdkLoaded, disabled, initializeButton]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupPayPalButton();
        };
    }, [cleanupPayPalButton]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={2} textAlign="center">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box 
            ref={containerRef}
            sx={{ 
                minHeight: '100px',
                display: disabled ? 'none' : 'block'
            }}
        />
    );
};

export default PayPalButton; 